import {
  countBolts,
  createSimState,
  type GameModule,
  type ParsedLevel,
  parseLevel,
  type SimState,
  snapshot,
  tick,
} from '@md/engine';
import {
  type DuoInputs,
  NEUTRAL_INPUTS,
  type PlayerIndex,
  type PlayerInputs,
  type SimStateSnapshot,
  TICK_RATE,
  withInputs,
} from '@md/shared';
import type { GameResult, GameSession } from '../game/session';

/**
 * Session de jeu LOCALE : un clavier, deux joueurs, zéro réseau (spec §5.2).
 *
 * Elle possède l'unique `SimState` du client et l'avance avec le `tick()`
 * déterministe du moteur. Deux régimes :
 *  - `auto` : la boucle de rendu appelle `advance(elapsedMs)`, un accumulateur
 *    consomme le temps réel par pas fixes de 1/60 s ;
 *  - `manuel` : plus aucun tick automatique, seuls les appels à `step(n)`
 *    font avancer la simulation. C'est le régime des tests E2E (spec §9).
 */
export interface LocalSession extends GameSession {
  readonly module: GameModule;
  /** Charge un niveau (validé par zod) et repart du tick 0. */
  loadLevel(level: unknown): void;
  /** Recharge le niveau courant depuis le début. */
  restart(): void;
  /** Fixe la graine du PRNG et réinitialise le niveau courant. */
  seed(value: number): void;
  setInputs(player: PlayerIndex, patch: Partial<PlayerInputs>): void;
  /** Entrées armées pour le prochain tick. */
  inputs(): DuoInputs;
  /** Avance de N ticks et bascule en régime manuel. */
  step(ticks: number): void;
  /** Consomme du temps réel (régime auto uniquement). */
  advance(elapsedMs: number): void;
  auto(): boolean;
  setAuto(auto: boolean): void;
  state(): SimState | null;
  snapshot(): SimStateSnapshot | null;
  /** Notifié après chaque avancée de la simulation ou changement de niveau. */
  subscribe(listener: (snapshot: SimStateSnapshot) => void): () => void;
  /** Résultat de fin de niveau, `null` tant que le niveau n'est pas gagné. */
  result(): GameResult | null;
}

const TICK_MS = 1000 / TICK_RATE;
/** Plafond de rattrapage : évite la spirale de la mort après un gros lag. */
const MAX_TICKS_PER_FRAME = 5;

export function createLocalSession(module: GameModule): LocalSession {
  let level: ParsedLevel | null = null;
  let sim: SimState | null = null;
  let seedValue = 0;
  let inputs: DuoInputs = [NEUTRAL_INPUTS, NEUTRAL_INPUTS];
  let isAuto = true;
  let accumulator = 0;
  const listeners = new Set<(snap: SimStateSnapshot) => void>();

  /**
   * Instantané enrichi : `inputs` porte les entrées ARMÉES (celles du prochain
   * tick) et non celles du tick précédent. Le clavier étant lu en continu,
   * c'est la valeur qu'un test veut observer juste après un appui, sans avoir
   * à faire avancer la simulation.
   */
  const currentSnapshot = (): SimStateSnapshot | null =>
    sim ? { ...snapshot(sim), inputs } : null;

  const notify = (): void => {
    const snap = currentSnapshot();
    if (!snap) {
      return;
    }
    for (const listener of listeners) {
      listener(snap);
    }
  };

  const restartLevel = (): void => {
    if (!level) {
      return;
    }
    sim = createSimState(level, module, seedValue);
    inputs = [NEUTRAL_INPUTS, NEUTRAL_INPUTS];
    accumulator = 0;
    notify();
  };

  const runTicks = (count: number): void => {
    if (!sim) {
      return;
    }
    for (let i = 0; i < count; i += 1) {
      sim = tick(sim, inputs, module);
    }
    if (count > 0) {
      notify();
    }
  };

  return {
    module,

    loadLevel(input: unknown): void {
      level = parseLevel(input, { tileLegend: module.tileLegend });
      restartLevel();
    },

    restart(): void {
      restartLevel();
    },

    seed(value: number): void {
      seedValue = value;
      restartLevel();
    },

    setInputs(player: PlayerIndex, patch: Partial<PlayerInputs>): void {
      const next = withInputs(inputs[player], patch);
      inputs = player === 0 ? [next, inputs[1]] : [inputs[0], next];
    },

    inputs(): DuoInputs {
      return inputs;
    },

    step(ticks: number): void {
      isAuto = false;
      accumulator = 0;
      runTicks(Math.max(0, Math.floor(ticks)));
    },

    advance(elapsedMs: number): void {
      if (!isAuto || !sim) {
        return;
      }
      accumulator += Math.max(0, elapsedMs);
      const pending = Math.floor(accumulator / TICK_MS);
      if (pending > MAX_TICKS_PER_FRAME) {
        // Gros décrochage (onglet en arrière-plan…) : on abandonne le retard
        // plutôt que de rattraper des centaines de ticks d'un coup.
        accumulator = 0;
        runTicks(MAX_TICKS_PER_FRAME);
        return;
      }
      accumulator -= pending * TICK_MS;
      runTicks(pending);
    },

    auto(): boolean {
      return isAuto;
    },

    setAuto(auto: boolean): void {
      isAuto = auto;
      accumulator = 0;
    },

    state(): SimState | null {
      return sim;
    },

    snapshot(): SimStateSnapshot | null {
      return currentSnapshot();
    },

    result(): GameResult | null {
      if (sim?.status !== 'won') {
        return null;
      }
      return {
        timeMs: (sim.tick * 1000) / TICK_RATE,
        deaths: sim.deaths,
        bolts: countBolts(sim.entities),
        stars: sim.stars,
      };
    },

    subscribe(listener: (snap: SimStateSnapshot) => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
